/**
 * ExpertContentArticleCard Component
 * Displays expert content article with title, description, content preview, and optional link.
 * Clicking the card opens ArticleDetailModal with full content; "Open link" opens LinkModal (iframe).
 */

import React, { useState, useCallback, useMemo } from 'react'
import { Box, Typography } from '@mui/material'
import { useTranslation } from '../../shared/hooks/useIntractionTranslate'
import { INTERAX_COLORS, FONT_FAMILY_PRIMARY } from '../../constants/interaxColors'
import { ExpertContentArticle } from '../../../../pure-js/types/ExpertVideo'
import { draftJsContentToPlainText, isDraftJsContent } from '../../../../pure-js/libs/draftJsToHtml'
import { LinkModal } from './LinkModal'
import { ArticleDetailModal } from './ArticleDetailModal'
import ButtonPrimary from '../../interaxDesignSystemComponents/ButtonPrimary'

export interface ExpertContentArticleCardProps {
  article: ExpertContentArticle
  className?: string
}

const CONTENT_PREVIEW_MAX_LENGTH = 160

/**
 * ExpertContentArticleCard - Clickable card; opens ArticleDetailModal with full article; "Open link" opens LinkModal
 */
export const ExpertContentArticleCard: React.FC<ExpertContentArticleCardProps> = ({ article, className = '' }) => {
  const { t } = useTranslation()
  const [linkModalOpen, setLinkModalOpen] = useState(false)
  const [articleDetailModalOpen, setArticleDetailModalOpen] = useState(false)

  const openLinkModal = useCallback(() => setLinkModalOpen(true), [])
  const closeLinkModal = useCallback(() => setLinkModalOpen(false), [])
  const openArticleDetailModal = useCallback(() => setArticleDetailModalOpen(true), [])
  const closeArticleDetailModal = useCallback(() => setArticleDetailModalOpen(false), [])

  const displayDescription = useMemo(() => {
    if (article.descriptionDraft && isDraftJsContent(article.descriptionDraft)) {
      return draftJsContentToPlainText(article.descriptionDraft)
    }
    return article.description ?? ''
  }, [article.description, article.descriptionDraft])

  const displayContent = useMemo(() => {
    if (article.contentDraft && isDraftJsContent(article.contentDraft)) {
      return draftJsContentToPlainText(article.contentDraft)
    }
    return article.content ?? ''
  }, [article.content, article.contentDraft])

  const contentPreview =
    displayContent.length > CONTENT_PREVIEW_MAX_LENGTH
      ? `${displayContent.slice(0, CONTENT_PREVIEW_MAX_LENGTH)}...`
      : displayContent

  return (
    <>
      <Box
        role="button"
        tabIndex={0}
        onClick={openArticleDetailModal}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            openArticleDetailModal()
          }
        }}
        sx={{
          backgroundColor: INTERAX_COLORS.heritageBlue40,
          borderRadius: '8px',
          padding: '20px',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          height: '100%',
          minHeight: '280px',
          cursor: 'pointer',
          transition: 'background-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease',
          '&:hover': {
            backgroundColor: INTERAX_COLORS.heritageBlue,
            boxShadow: `0 8px 24px rgba(0, 0, 0, 0.35)`,
            transform: 'translateY(-2px)'
          },
          '&:focus': { outline: '2px solid ' + INTERAX_COLORS.sun, outlineOffset: 2 }
        }}
        className={className}
        aria-label={article.title}
      >
        {article.imageUrl && (
          <Box
            component="img"
            src={article.imageUrl}
            alt={article.title}
            sx={{
              width: '100%',
              height: 'auto',
              maxHeight: '200px',
              objectFit: 'cover',
              borderRadius: '8px',
              border: `1px solid ${INTERAX_COLORS.thunderGrey}`
            }}
          />
        )}

        <Typography
          sx={{
            fontFamily: FONT_FAMILY_PRIMARY,
            fontWeight: 500,
            fontSize: '20px',
            lineHeight: '28px',
            color: INTERAX_COLORS.white
          }}
        >
          {article.title}
        </Typography>

        {displayDescription && (
          <Typography
            sx={{
              fontFamily: FONT_FAMILY_PRIMARY,
              fontSize: '14px',
              lineHeight: '20px',
              color: INTERAX_COLORS.thunderGrey
            }}
          >
            {displayDescription}
          </Typography>
        )}

        {contentPreview && (
          <Typography
            sx={{
              fontFamily: FONT_FAMILY_PRIMARY,
              fontSize: '14px',
              lineHeight: '20px',
              color: INTERAX_COLORS.white,
              flex: 1
            }}
          >
            {contentPreview}
          </Typography>
        )}

        {article.link && (
          <Box sx={{ mt: 'auto' }} onClick={(e) => e.stopPropagation()} role="presentation">
            <ButtonPrimary onClick={openLinkModal} size="medium">
              {t('landing.knowledgeHub.openLink') || 'Open link'}
            </ButtonPrimary>
          </Box>
        )}
      </Box>

      <ArticleDetailModal isOpen={articleDetailModalOpen} onClose={closeArticleDetailModal} article={article} />

      {article.link && (
        <LinkModal
          isOpen={linkModalOpen}
          onClose={closeLinkModal}
          url={article.link}
          title={article.title}
          description={displayDescription}
        />
      )}
    </>
  )
}
